import { ErrorCode } from '@trulyinbox/errors';
import {
  BILLING_CYCLES,
  BILLING_CYCLE_TO_DAYS,
  BILLING_MANAGED_BY,
  type BillingCycle,
} from '@trulyinbox/types';
import { ApiError } from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatCurrency, formatNumber } from '@/lib/format';
import { formatDate, toDate } from '@/lib/date';
import type { BillingPlan, PreviewResponse, Subscription } from '@/lib/billing';
import type { LocalizedPrice } from '@/features/billing/use-price-preview';

/**
 * Non-component billing-domain logic for the Upgrade surface. Lives apart from
 * the `.tsx` so the card stays a Vite Fast Refresh boundary — limit summaries,
 * the purchasable predicate, and error-code branching all belong here, never
 * inline in a component.
 */

/** A plan is purchasable from the client only when it has a checkout price id. */
export function isPurchasable(plan: BillingPlan): boolean {
  return plan.paddleCheckoutPriceId != null;
}

/**
 * The perpetual `free` plan. A free org has NO live paid subscription, so every
 * paid tier is a first-time checkout — never the portal. Keyed on `code` (the
 * catalog's stable business key), falling back to the stored `name`, mirroring
 * `shouldShowUpgrade` in `PlanCard.tsx`.
 */
export const FREE_PLAN_CODE = 'free';

export function isFreePlan(plan: BillingPlan): boolean {
  return (plan.code ?? plan.name).toLowerCase() === FREE_PLAN_CODE;
}

/**
 * The plans the org owner can actually upgrade to: public (`isPublic`),
 * purchasable (has a checkout price id), and not the org's current plan.
 * Free/trial and any hidden or non-purchasable plan are dropped, so the Upgrade
 * card only ever lists real checkout targets — there is no muted placeholder row.
 */
export function upgradeTargets(
  plans: BillingPlan[],
  currentPlanId: number | null,
): BillingPlan[] {
  return plans.filter(
    (plan) => plan.isPublic && isPurchasable(plan) && plan.id !== currentPlanId,
  );
}

/**
 * One-line summary of a plan's hard limits, e.g. "25 inboxes · 1,000 warmup
 * emails/day". Both limits come from plan restrictions, where `null` means
 * UNLIMITED — so `maxEmailAccounts === null` and `aggregateDailyWarmup === null`
 * are each unmetered and must render as "Unlimited", never as a formatted
 * `null`. A plan that is unmetered on BOTH collapses to a bare "Unlimited"
 * rather than repeating the word on either side of the separator.
 */
export function planLimitsSummary(plan: BillingPlan): string {
  if (plan.maxEmailAccounts == null && plan.aggregateDailyWarmup == null) {
    return 'Unlimited';
  }
  const inboxes =
    plan.maxEmailAccounts == null
      ? 'Unlimited inboxes'
      : `${formatNumber(plan.maxEmailAccounts)} inboxes`;
  const warmup =
    plan.aggregateDailyWarmup == null
      ? 'Unlimited'
      : formatNumber(plan.aggregateDailyWarmup);
  return `${inboxes} · ${warmup} warmup emails/day`;
}

/**
 * Human-readable plan name for the UI. The catalog stores `name = code`
 * (e.g. `starter-monthly-05-26`) because the tier display names ("Starter"
 * repeats across monthly/quarterly/yearly) collide under the unique `name`
 * constraint — so derive a "Tier · Cycle" label from the code. `free` / `trial`
 * and any code that doesn't match the base-plan shape fall back to the stored
 * name, so an unrecognised catalog never renders blank.
 */
export function planDisplayName(plan: BillingPlan): string {
  const code = plan.code ?? plan.name;
  if (code === 'free') return 'Free';
  if (code === 'trial') return 'Trial';
  const match = code.match(PLAN_CODE_RE);
  if (!match) return plan.name;
  return `${titleizeSlug(match[1])} · ${titleizeSlug(match[2])}`;
}

/** Title-case a hyphenated slug segment ("scale-plus" → "Scale Plus"). Shared by
 * `planDisplayName` and the tier-card helpers below so casing never diverges. */
function titleizeSlug(segment: string): string {
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Matches the catalog `<tier>-<cycle>-MM-YY` code shape (splits tier vs cycle). */
const PLAN_CODE_RE = /^(.+)-(monthly|quarterly|yearly)-\d{2}-\d{2}$/;

/**
 * `true` when a checkout failure is the expected "this plan can't be purchased
 * right now" condition (e.g. the seed still uses placeholder Paddle ids). Branch
 * on the catalog code, never on the message string.
 */
export function isPlanNotPurchasable(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.errorCode === ErrorCode.Billing.Checkout.PlanNotPurchasable
  );
}

/**
 * `true` when the portal request failed because **no portal exists** for this org
 * — `040501 NoActiveSubscription`, raised when the org has no Paddle customer id
 * because it has never been billed.
 *
 * This is a permanent state, not a transient failure: retrying will never help,
 * so the caller must replace the control with an explanation rather than leave a
 * button that can only fail. It is genuinely reachable — `upgradeRowMode` returns
 * `portal` whenever the subscription read errors, which includes orgs that have
 * no Paddle relationship at all.
 *
 * Branch on the catalog code, never on the message string.
 */
export function isNoBillingPortal(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.errorCode === ErrorCode.Billing.CustomerPortal.NoActiveSubscription
  );
}

/**
 * How a single upgrade-target row behaves for the current org (P2, spec §8):
 *   - `checkout` — genuinely no live Paddle subscription (free/trial/manual with
 *     `paddleSubscriptionId == null`): a first-time purchase runs through the
 *     Paddle.js checkout overlay, NOT the in-place PATCH.
 *   - `upgrade`  — an ACTIVE paid subscriber moving to a strictly higher-priced
 *     plan in the same currency: the in-place proration-preview → PATCH flow.
 *   - `portal`   — everything safe-by-default: a downgrade / same-or-lower price,
 *     a different currency, a non-ACTIVE subscriber (TRIALING/PAUSED/CANCELED),
 *     or a subscription read that ERRORED. Handled in the billing portal — we
 *     never open a fresh checkout for an org that already has a subscription.
 *
 * The price comparison uses the CURRENT PLAN's `basePrice` (looked up from the
 * plan catalog by the org's current plan id), NOT the subscription's discounted
 * `unitPrice` — a discount must not make a genuinely-lower plan look like an
 * upgrade. This mirrors the backend's upgrade-eligibility rule exactly.
 *
 * GUARDRAIL — do NOT feed localized (`PricePreview`) prices in here. This
 * function and the `currency`/`basePrice` comparison below mirror the backend's
 * `canUpgrade`, which runs on the flat USD catalog. Localized amounts are
 * DISPLAY ONLY: swapping them in would desync this button from what
 * `PATCH /billing/subscription` actually accepts, so an org would be offered an
 * in-place upgrade the server then rejects. See `formatPlanMonthlyPrice` for the
 * display path, which is the only place a localized price belongs.
 */
export type UpgradeRowMode = 'checkout' | 'upgrade' | 'portal' | 'managed';

/**
 * Why a plan CTA is locked for this org, or null when it isn't. A Saleshandy
 * plan can't be changed here at all; an APUS-assigned plan can only move up.
 */
export function managedBillingReason(subscription: Subscription | null | undefined): string | null {
  switch (subscription?.billingManagedBy) {
    case BILLING_MANAGED_BY.SALESHANDY:
      return 'Your plan is managed in Saleshandy. Change it from your Saleshandy account.';
    case BILLING_MANAGED_BY.INTERNAL:
      return 'Your plan was set up by our team, so you can only move to a higher plan. Contact support to move to a lower one.';
    default:
      return null;
  }
}

export function upgradeRowMode(params: {
  target: BillingPlan;
  /** The org's current plan, resolved from the catalog by the current plan id. */
  currentPlan: BillingPlan | null;
  subscription: Subscription | null | undefined;
  /** The `GET /billing/subscription` read failed — degrade to `portal`, safe. */
  subscriptionErrored: boolean;
}): UpgradeRowMode {
  const { target, currentPlan, subscription, subscriptionErrored } = params;

  // Ahead of everything: a managed org has no Paddle subscription, so the
  // branches below would hand it a brand-new checkout for every tier. Saleshandy
  // can't change plan here at all; an APUS-assigned plan may only check out a
  // strictly pricier one (mirrors `createCheckout`'s `canUpgrade` gate).
  if (subscription?.billingManagedBy === BILLING_MANAGED_BY.SALESHANDY) return 'managed';
  if (subscription?.billingManagedBy === BILLING_MANAGED_BY.INTERNAL) {
    return currentPlan != null && isHigherPricedPurchase(target, currentPlan) ? 'checkout' : 'managed';
  }

  // A free org has NO live paid subscription, so every paid tier is a first-time
  // Paddle.js checkout — never the portal. This holds even when the subscription
  // read errored, or still carries a stale `paddleSubscriptionId` from a past,
  // now-canceled subscription (cancel → `downgradeToFree` sets plan=free +
  // status=CANCELED but keeps the id). Deciding on the *current plan being free*
  // — not on `paddleSubscriptionId`/status — is what fixes the "Manage billing"
  // dead-end on every tier for a free org, and it's placed ahead of the
  // `subscriptionErrored` guard because a free org has nothing to double-charge.
  if (currentPlan != null && isFreePlan(currentPlan)) return 'checkout';

  // A failed subscription read leaves the org's paid status unknown. The summary
  // may show them as paid, so opening a NEW checkout would double-charge — route
  // to the portal instead of guessing `checkout`.
  if (subscriptionErrored) return 'portal';

  // Genuinely no subscription (free / trial / manual) → first-time checkout.
  if (!subscription || subscription.paddleSubscriptionId == null) return 'checkout';

  // A subscriber whose subscription isn't ACTIVE (trialing / paused / canceled)
  // can't upgrade in place — the PATCH would 041003. Route them to the portal to
  // fix payment / resume first.
  if (subscription.status !== 'ACTIVE') return 'portal';

  // ACTIVE paid subscriber: eligible only when the target is a strictly
  // higher-priced, same-currency, public, purchasable plan (mirrors backend).
  if (currentPlan != null && isHigherPricedPurchase(target, currentPlan)) {
    return 'upgrade';
  }
  return 'portal';
}

/** The backend's `canUpgrade` rule: public, purchasable, same currency, strictly pricier. */
function isHigherPricedPurchase(target: BillingPlan, currentPlan: BillingPlan): boolean {
  return (
    target.isPublic &&
    isPurchasable(target) &&
    target.id !== currentPlan.id &&
    target.currency === currentPlan.currency &&
    target.basePrice != null &&
    currentPlan.basePrice != null &&
    Number(target.basePrice) > Number(currentPlan.basePrice)
  );
}

/**
 * How the UI should react to a `PATCH /billing/subscription` (or checkout)
 * failure. Branches on the 6-digit `errorCode` from `@trulyinbox/errors`, never
 * on message text:
 *   - `portal`      — `041005 PaymentFailed` (Paddle rejected the prorated
 *     charge → fix payment method) and `041001 DowngradeNotAllowed`: the change
 *     can only be completed in the billing portal. Message is the server's.
 *   - `checkout`    — `041002 NoPaddleSubscription`: there is no live
 *     subscription to modify, so route to the first-time Paddle.js checkout.
 *   - `inProgress`  — `041007 AlreadyInProgress`: a per-org upgrade lock is held
 *     (concurrent submit / charge→webhook window). Disable submit and DON'T
 *     auto-retry — the caller must wait and refresh.
 *   - `unavailable` — `040701 PlanNotPurchasable`: this plan can't be purchased
 *     right now. Not a downgrade — the target is simply not selectable.
 *   - `retry`       — `041008 UpgradeFailed` (transient Paddle 5xx),
 *     `041006 PreviewFailed`, `041004 NoChange`, `041003`, and any other
 *     failure: a generic, retryable inline message.
 */
export type UpgradeErrorAction =
  | { kind: 'portal'; message: string }
  | { kind: 'checkout'; message: string }
  | { kind: 'inProgress'; message: string }
  | { kind: 'unavailable'; message: string }
  | { kind: 'retry'; message: string };

export function classifyUpgradeError(err: unknown): UpgradeErrorAction {
  const code = err instanceof ApiError ? err.errorCode : null;
  switch (code) {
    case ErrorCode.Billing.Upgrade.PaymentFailed:
    case ErrorCode.Billing.Upgrade.DowngradeNotAllowed:
      return {
        kind: 'portal',
        message: getErrorMessage(err, 'Manage this change in the billing portal.'),
      };
    case ErrorCode.Billing.Upgrade.NoPaddleSubscription:
      return {
        kind: 'checkout',
        message: getErrorMessage(err, 'Start a checkout to subscribe first.'),
      };
    case ErrorCode.Billing.Upgrade.AlreadyInProgress:
      return {
        kind: 'inProgress',
        message: getErrorMessage(
          err,
          'An upgrade is already in progress. Refresh in a moment.',
        ),
      };
    case ErrorCode.Billing.Checkout.PlanNotPurchasable:
      return {
        kind: 'unavailable',
        message: getErrorMessage(err, "This plan isn't available right now."),
      };
    default:
      return {
        kind: 'retry',
        message: getErrorMessage(err, "We couldn't complete your upgrade. Please try again."),
      };
  }
}

/**
 * User-facing message for a `POST /billing/subscription/redeem-code` failure.
 * Branches on the 6-digit `errorCode` from `@trulyinbox/errors`, never on
 * message text (same pattern as `classifyUpgradeError` above). Anything outside
 * the known `Discount` conditions falls back to the server's own message, then a
 * generic retryable line.
 */
export function redeemCodeErrorMessage(err: unknown): string {
  const code = err instanceof ApiError ? err.errorCode : null;
  switch (code) {
    case ErrorCode.Billing.Discount.NotFound:
      return "We couldn't find that code.";
    case ErrorCode.Billing.Discount.Inactive:
      return 'This code is no longer active or has expired.';
    case ErrorCode.Billing.Discount.NotApplicable:
      return "This code can't be applied to your plan.";
    case ErrorCode.Billing.Discount.NoPaddleSubscription:
      return 'You need an active paid subscription to redeem a code.';
    case ErrorCode.Billing.Discount.AlreadyRedeemed:
      return "You've already used this code.";
    case ErrorCode.Billing.Discount.NotRedeemableInApp:
      return 'This code is applied at checkout.';
    case ErrorCode.Billing.Discount.PaddleSyncFailed:
      return 'Something went wrong applying the code. Please try again.';
    default:
      return getErrorMessage(err, "We couldn't apply that code. Please try again.");
  }
}

/**
 * The line items of a proration preview, formatted for display. Credit and
 * discount are shown as negative because they reduce the amount charged now;
 * both are omitted when zero. `amountToPay` is flagged `emphasis` (the total).
 * All money uses the preview's own currency via the shared `formatCurrency`.
 */
export type ProrationRow = { label: string; value: string; emphasis?: boolean };

export function buildProrationRows(preview: PreviewResponse): ProrationRow[] {
  const money = (amount: number) => formatCurrency(amount, preview.currency);
  // No immediate charge: the prorated amount rolls into the next invoice, so
  // every money field is 0. Don't render a misleading "$0.00 now" breakdown —
  // collapse to a single line pointing at the next bill (F7).
  if (!preview.hasImmediateCharge) {
    return [
      {
        label: 'Prorated on your next bill',
        value: preview.nextBilledAt ? formatDate(toDate(preview.nextBilledAt)) : '-',
        emphasis: true,
      },
    ];
  }
  // `subtotal` is the GROSS plan amount (pre-tax, pre-credit) — labelling it
  // "Subtotal" next to an itemised credit reads as though the credit were
  // deducted twice.
  const rows: ProrationRow[] = [{ label: 'Plan amount', value: money(preview.subtotal) }];
  if (preview.discount > 0) {
    rows.push({ label: 'Discount', value: `−${money(preview.discount)}` });
  }
  if (preview.credit > 0) {
    rows.push({ label: 'Unused current plan credit', value: `−${money(preview.credit)}` });
  }
  rows.push({ label: 'Tax', value: money(preview.tax) });
  rows.push({ label: 'Charged now', value: money(preview.amountToPay), emphasis: true });
  return rows;
}

// ── Tier-card pricing grid (2026-07 billing redesign) ───────────────────────
//
// The flat upgrade-row listing was replaced by a card-per-TIER grid. These pure
// helpers turn the cycle-variant plan catalog into ordered tier cards, resolve
// the per-cycle (and Scale Plus per-volume) plan, and classify each card's CTA —
// the `.tsx` never derives any of this inline.

/** Billing cycles the pricing grid offers, in display order. Sourced from
 * `@trulyinbox/types` — the one home shared with apps/api, apps/admin, and the
 * `plans.billingCycle` DB enum — and re-exported so feature files keep importing
 * cycle types from `plan-helpers` as they do today. */
export { BILLING_CYCLES };
export type { BillingCycle };

/** Months per billing cycle — normalizes a plan's `basePrice` to a per-month figure. */
export const CYCLE_MONTHS: Record<BillingCycle, number> = {
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

/** `plans.validityInDays` → billing cycle (fallback when the code isn't cycle-shaped).
 * Derived from the shared `BILLING_CYCLE_TO_DAYS` map so it can't drift from the
 * cycle set or from what the seed writes. */
const VALIDITY_TO_CYCLE: Record<number, BillingCycle> = Object.fromEntries(
  (Object.entries(BILLING_CYCLE_TO_DAYS) as Array<[BillingCycle, number]>).map(
    ([cycle, days]) => [days, cycle],
  ),
) as Record<number, BillingCycle>;

/** Canonical tier order for the pricing grid. Unknown tiers sort after these. */
export const TIER_ORDER = ['starter', 'growth', 'scale', 'scale-plus'] as const;

/** The tier whose card carries the "Most Popular" badge. */
export const POPULAR_TIER_KEY = 'growth';

const KNOWN_TIER_LABEL: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
  'scale-plus': 'Scale Plus',
};

/** `scale-plus-5k` → volume 5. Folds the three volume SKUs into the one tier. */
const SCALE_PLUS_SLUG_RE = /^scale-plus-(\d+)k$/;

type PlanTierParts = { tierSlug: string; cycle: BillingCycle };

/** `{ tierSlug, cycle }` parsed from a plan `code`, or null for a code that
 * doesn't match the `<tier>-<cycle>-MM-YY` shape (free/trial/legacy `pro`). */
function planTierParts(plan: BillingPlan): PlanTierParts | null {
  const code = plan.code ?? plan.name;
  const match = code.match(PLAN_CODE_RE);
  if (!match) return null;
  return { tierSlug: match[1], cycle: match[2] as BillingCycle };
}

/**
 * The raw tier slug of a plan `code` — `scale-plus-10k-yearly-05-26` →
 * `scale-plus-10k`, volume SKU intact (unlike `planTierKey`, which folds every
 * Scale Plus volume into one card). Null for a code outside the catalog shape
 * (free / trial / custom / legacy), and for a missing code.
 */
export function planCodeTierSlug(code: string | null | undefined): string | null {
  return code?.match(PLAN_CODE_RE)?.[1] ?? null;
}

/**
 * The plan heading on the Billing › Overview band: the tier name with the cycle
 * stripped ("Growth", "Scale Plus"), since the cycle rides its own chip beside
 * it. Anything outside the catalog shape keeps its stored name, so a custom or
 * legacy plan never renders blank.
 */
export function planTitle(plan: { name: string; code?: string | null }): string {
  const code = plan.code ?? plan.name;
  if (code === FREE_PLAN_CODE) return 'Free';
  if (code === 'trial') return 'Trial';
  const slug = planCodeTierSlug(code);
  return slug ? tierForSlug(slug).label : plan.name;
}

/** Tier identity for a tier slug, folding every `scale-plus-Nk` into one tier. */
function tierForSlug(slug: string): {
  key: string;
  label: string;
  isScalePlus: boolean;
  volume: number | null;
} {
  const sp = slug.match(SCALE_PLUS_SLUG_RE);
  if (sp) {
    return { key: 'scale-plus', label: 'Scale Plus', isScalePlus: true, volume: Number(sp[1]) * 1000 };
  }
  if (slug === 'scale-plus') {
    return { key: 'scale-plus', label: 'Scale Plus', isScalePlus: true, volume: null };
  }
  return { key: slug, label: KNOWN_TIER_LABEL[slug] ?? titleizeSlug(slug), isScalePlus: false, volume: null };
}

type ClassifiedPlan = {
  tierKey: string;
  tierName: string;
  isScalePlus: boolean;
  cycle: BillingCycle;
  /** Scale Plus daily-warmup volume (from the code); null for single-volume tiers. */
  volume: number | null;
};

/** Derive a plan's tier + cycle + (Scale Plus) volume. A code that doesn't match
 * the catalog shape (legacy `pro`/`scale`, `free`) becomes its own single-cycle
 * tier keyed by code/name — so the grid degrades gracefully on old catalogs. */
function classifyPlan(plan: BillingPlan): ClassifiedPlan {
  const parts = planTierParts(plan);
  if (parts) {
    const tier = tierForSlug(parts.tierSlug);
    return {
      tierKey: tier.key,
      tierName: tier.label,
      isScalePlus: tier.isScalePlus,
      cycle: parts.cycle,
      volume: tier.volume,
    };
  }
  return {
    tierKey: (plan.code ?? plan.name).toLowerCase(),
    tierName: planDisplayName(plan),
    isScalePlus: false,
    cycle: planCycle(plan),
    volume: null,
  };
}

/** A plan's billing cycle: code-derived, else `validityInDays`-derived, else monthly. */
export function planCycle(plan: BillingPlan): BillingCycle {
  const fromValidity = plan.validityInDays != null ? VALIDITY_TO_CYCLE[plan.validityInDays] : undefined;
  return planTierParts(plan)?.cycle ?? fromValidity ?? 'monthly';
}

/** Stable tier key for a plan — used to mark the org's current tier's card. */
export function planTierKey(plan: BillingPlan): string {
  return classifyPlan(plan).tierKey;
}

/** Tier display name with the billing cycle (and Scale Plus volume) stripped:
 * "Starter", "Scale Plus", "Pro". Drives the pricing header's current-plan copy. */
export function planTierName(plan: BillingPlan): string {
  return classifyPlan(plan).tierName;
}

/** A plan's Scale Plus daily-warmup volume (`scale-plus-10k-…` → 10000), or null
 * for every single-volume tier. Seeds the Scale Plus card's slider at the org's
 * own volume instead of the smallest step. */
export function planVolume(plan: BillingPlan): number | null {
  return classifyPlan(plan).volume;
}

/** Visitors in this country (Paddle's IP-resolved `countryCode`) don't see the
 * Starter tier on the monthly cycle — it is offered quarterly / annually only. */
const STARTER_MONTHLY_HIDDEN_COUNTRY = 'US';

/** Whether to drop a tier's card from the grid for this visitor + cycle. Fails
 * OPEN: an unresolved country (Paddle blocked / unconfigured) hides nothing, and
 * the org's own current tier is never hidden so its "Current" card stays put. */
export function isTierHiddenForVisitor(
  tierKey: string,
  cycle: BillingCycle,
  countryCode: string | null,
  currentTierKey: string | null,
): boolean {
  return (
    tierKey === 'starter' &&
    cycle === 'monthly' &&
    countryCode === STARTER_MONTHLY_HIDDEN_COUNTRY &&
    currentTierKey !== tierKey
  );
}

/** One tier's card: its identity + every public/purchasable plan across cycles. */
export type TierCardGroup = {
  tierKey: string;
  tierName: string;
  isScalePlus: boolean;
  plans: BillingPlan[];
};

function minBasePrice(plans: BillingPlan[]): number {
  return Math.min(...plans.map((p) => (p.basePrice != null ? Number(p.basePrice) : Infinity)));
}

/** Group the catalog into ordered tier cards, folding the three `scale-plus-*`
 * volumes into one "Scale Plus" card. Keeps only public + purchasable plans (the
 * `upgradeTargets` filter spirit) but — unlike `upgradeTargets` — KEEPS the
 * current tier so its card can render as "Current". Order: `TIER_ORDER`, then any
 * unknown tiers by ascending price. */
export function groupPlansIntoTierCards(plans: BillingPlan[]): TierCardGroup[] {
  const byTier = new Map<string, TierCardGroup>();
  for (const plan of plans) {
    if (!plan.isPublic || !isPurchasable(plan)) continue;
    const c = classifyPlan(plan);
    const group = byTier.get(c.tierKey);
    if (group) group.plans.push(plan);
    else byTier.set(c.tierKey, { tierKey: c.tierKey, tierName: c.tierName, isScalePlus: c.isScalePlus, plans: [plan] });
  }
  const orderIndex = (key: string) => {
    const i = TIER_ORDER.indexOf(key as (typeof TIER_ORDER)[number]);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return [...byTier.values()].sort((a, b) => {
    const byOrder = orderIndex(a.tierKey) - orderIndex(b.tierKey);
    return byOrder !== 0 ? byOrder : minBasePrice(a.plans) - minBasePrice(b.plans);
  });
}

/** The plan matching `cycle` (and, for Scale Plus, `volume`) within a tier, or null. */
export function selectTierPlan(
  group: TierCardGroup,
  cycle: BillingCycle,
  volume: number | null,
): BillingPlan | null {
  return (
    group.plans.find((p) => {
      const c = classifyPlan(p);
      if (c.cycle !== cycle) return false;
      return group.isScalePlus ? c.volume === volume : true;
    }) ?? null
  );
}

/**
 * What this tier actually saves on `cycle` versus paying monthly, as a whole
 * percent — or null when there's nothing honest to claim (monthly itself, a
 * missing plan on either side, a non-positive price, or a discount that rounds
 * to 0%).
 *
 * Computed PER TIER from the live catalog, never a fixed marketing string. The
 * toggle used to carry hardcoded "Save 10%" / "Save 20%" pills, and the yearly
 * one was simply false: against the seeded prices, yearly is ~15% off on five of
 * six tiers, while `starter` is ~24%. One static number cannot be right for all
 * of them, and a number derived from the prices can never drift from what the
 * customer is actually charged.
 *
 * Compares TOTAL PER TERM, not per-month: `monthlyPrice × months(cycle)` is what
 * the same period costs billed monthly, and `basePrice` on the cycle plan is what
 * it costs billed on that cycle.
 *
 * Reads `basePrice` (catalog USD) DELIBERATELY, even beside localized prices:
 * with no per-currency overrides in Paddle, localization is a pure proportional
 * FX conversion, so the percentage is mathematically identical either way — the
 * badge cannot contradict the rupee figures next to it. **Revisit if per-currency
 * price overrides are ever added**: a hand-entered, non-proportional INR ladder
 * would make this USD-derived number wrong, and it would then have to recompute
 * from the localized amounts.
 */
export function cycleSavingsPercent(
  group: TierCardGroup,
  cycle: BillingCycle,
  volume: number | null,
): number | null {
  if (cycle === 'monthly') return null;

  const monthlyPlan = selectTierPlan(group, 'monthly', volume);
  const cyclePlan = selectTierPlan(group, cycle, volume);
  if (!monthlyPlan || !cyclePlan) return null;

  const monthlyPrice = monthlyPlan.basePrice != null ? Number(monthlyPlan.basePrice) : NaN;
  const cyclePrice = cyclePlan.basePrice != null ? Number(cyclePlan.basePrice) : NaN;
  if (!Number.isFinite(monthlyPrice) || !Number.isFinite(cyclePrice) || monthlyPrice <= 0) {
    return null;
  }

  const monthlyEquivalentTotal = monthlyPrice * CYCLE_MONTHS[cycle];
  const percent = Math.round(((monthlyEquivalentTotal - cyclePrice) / monthlyEquivalentTotal) * 100);
  // A zero/negative "saving" is not a selling point — render nothing instead.
  return percent > 0 ? percent : null;
}

/**
 * Font size (px) for a pricing card's headline figure, stepping down as the
 * formatted price gets longer so the figure, "/Month" and the "Save x%" chip
 * always share one line at the grid's narrowest card (260px → ~220px inside).
 *
 * The budget is what that row leaves the figure — ~114px after "/Month", the
 * chip and the gaps — at ~0.52em per character in the heavy tabular face. It is
 * sized as if the chip were always present, so toggling the billing cycle never
 * changes the figure's size. Short prices ("$99.00") keep the full 30px; a
 * long localized one ("₹1,24,999.00") bottoms out at 16px rather than vanish.
 */
export function planPriceFontSize(priceText: string): number {
  const FIGURE_BUDGET_PX = 114;
  const EM_PER_CHAR = 0.52;
  const fitted = Math.floor(FIGURE_BUDGET_PX / (EM_PER_CHAR * Math.max(1, priceText.length)));
  return Math.min(30, Math.max(16, fitted));
}

/**
 * The Scale Plus picker's last option: a volume above the largest SKU, which is
 * arranged with sales rather than bought from the grid. A sentinel beside the
 * numeric volumes, so "custom" can never be mistaken for a purchasable step.
 */
export const CUSTOM_VOLUME = 'custom';
export type VolumeChoice = number | typeof CUSTOM_VOLUME;

/** Where "Talk to sales" on the custom-volume card sends the customer — a booking page. */
export const SCALE_PLUS_CUSTOM_VOLUME_URL = 'https://calendly.com/trulyinbox-sales/30min';

/** Ascending list of the Scale Plus daily-warmup volumes offered (picker options). */
export function scalePlusVolumes(group: TierCardGroup): number[] {
  const vols = new Set<number>();
  for (const p of group.plans) {
    const v = classifyPlan(p).volume;
    if (v != null) vols.add(v);
  }
  return [...vols].sort((a, b) => a - b);
}

/** Per-month price for a plan, normalized across its billing cycle, or null. */
export function planMonthlyPrice(plan: BillingPlan): number | null {
  if (plan.basePrice == null) return null;
  return Number(plan.basePrice) / CYCLE_MONTHS[planCycle(plan)];
}

/**
 * The cheapest purchasable plan that grants `identifier`, or `null` when no plan
 * in the catalog does (an unrecognised identifier, a catalog that predates the
 * `resourceIdentifiers` field, or a plans read that failed).
 *
 * Drives "Unlocks on <plan>" on every locked feature. Plan-catalog data only —
 * this answers *which plan unlocks X*, never *is this org entitled to X*
 * (that's `useCapabilities()`).
 *
 * ## The ordering rule
 *
 * 1. Drop anything not {@link isPurchasable} — a plan with no checkout price id
 *    can't be bought, so naming it is a dead end.
 * 2. Pick the cheapest granting TIER, by the lowest {@link planMonthlyPrice}
 *    across that tier's granting plans. Per-month is the figure the pricing grid
 *    renders, so the tier we name is the one the customer sees as cheapest.
 * 3. **Ties across billing cycles resolve to the SHORTEST cycle.** Every cycle of
 *    a tier renders the same {@link planTierName}, so the *sentence* is identical
 *    either way — but the resolved plan is also the purchase target handed to the
 *    upgrade flow, and monthly is both the smallest first commitment and the
 *    lowest total charge. Sorting on per-month price alone would have picked the
 *    yearly SKU (yearly is always cheapest per month), quietly turning a "unlock
 *    this feature" click into an annual invoice.
 * 4. Remaining ties (two tiers priced identically, or a tier whose plans carry no
 *    price at all) fall back to {@link TIER_ORDER}, then ascending plan `id` — so
 *    the answer never depends on the order the catalog happened to arrive in.
 */
export function resolveUnlockingPlan(
  plans: BillingPlan[],
  identifier: string,
): BillingPlan | null {
  const granting = plans.filter(
    (plan) => isPurchasable(plan) && plan.resourceIdentifiers.includes(identifier),
  );
  if (granting.length === 0) return null;

  // Cheapest per-month price seen anywhere in a tier — step 2's tier ranking.
  const tierPrice = new Map<string, number>();
  for (const plan of granting) {
    const key = planTierKey(plan);
    const price = planMonthlyPrice(plan) ?? Infinity;
    tierPrice.set(key, Math.min(tierPrice.get(key) ?? Infinity, price));
  }

  const tierRank = (key: string) => {
    const i = TIER_ORDER.indexOf(key as (typeof TIER_ORDER)[number]);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };

  return [...granting].sort((a, b) => {
    const aKey = planTierKey(a);
    const bKey = planTierKey(b);
    const byTierPrice = (tierPrice.get(aKey) ?? Infinity) - (tierPrice.get(bKey) ?? Infinity);
    if (byTierPrice !== 0) return byTierPrice;
    const byCycle = CYCLE_MONTHS[planCycle(a)] - CYCLE_MONTHS[planCycle(b)];
    if (byCycle !== 0) return byCycle;
    const byTierOrder = tierRank(aKey) - tierRank(bKey);
    return byTierOrder !== 0 ? byTierOrder : a.id - b.id;
  })[0];
}

/**
 * Formatted per-month price ("$29.00" / "₹2,468.13"), or "—" when the plan
 * carries no price at all.
 *
 * `localized` is Paddle's geo-resolved quote for this plan's checkout price id
 * (`use-price-preview.ts`). When present it WINS over the catalog: both the
 * amount and the currency come from it, so a rupee number can never be rendered
 * behind a dollar sign. When absent — Paddle unreachable, blocked, or a
 * placeholder price id — this falls back to the flat USD catalog and the caller
 * shows the "Prices shown in USD" notice.
 *
 * Paddle prices a whole BILLING TERM, exactly like `basePrice`, so the
 * per-month division is the same on both paths.
 *
 * This is the ONLY money formatter the pricing cards and dialogs call. The bug
 * that buys is one stray component rendering `$` in front of an INR number.
 */
export function formatPlanMonthlyPrice(
  plan: BillingPlan,
  localized?: LocalizedPrice | null,
): string {
  const price =
    localized != null
      ? localized.amount / CYCLE_MONTHS[planCycle(plan)]
      : planMonthlyPrice(plan);
  return price == null ? '-' : formatCurrency(price, localized?.currency ?? plan.currency);
}

/** Static marketing line every tier card shows for its inbox allowance. */
const UNLIMITED_ACCOUNTS_LINE = 'Connect unlimited email accounts';

/** The feature/limit bullet lines for a tier card, in display order. Reuses
 * `formatNumber`; renders `null` metered limits as "Unlimited" (consistent with
 * `planLimitsSummary`) and OMITS the reply-rate line when the plan is uncapped. */
export function planCardFeatureLines(plan: BillingPlan): string[] {
  return planCardLimitItems(plan).map((item) => item.label);
}

/** Which plan limit a card line states — keys the tooltip copy in `plan-features`. */
export type PlanLimitKey = 'aggregate' | 'replyRate' | 'accounts';

/** The limit lines as `{ key, label }`, so the card can attach a tooltip per limit. */
export function planCardLimitItems(plan: BillingPlan): Array<{ key: PlanLimitKey; label: string }> {
  const items: Array<{ key: PlanLimitKey; label: string }> = [];
  items.push({
    key: 'aggregate',
    label:
      plan.aggregateDailyWarmup == null
        ? 'Unlimited warmup emails/day'
        : `${formatNumber(plan.aggregateDailyWarmup)} warmup emails/day`,
  });
  if (plan.replyRateCap != null) {
    items.push({ key: 'replyRate', label: `Up to ${plan.replyRateCap}% reply rate` });
  }
  items.push({ key: 'accounts', label: UNLIMITED_ACCOUNTS_LINE });
  return items;
}

/**
 * The CTA a tier card shows, by kind:
 *   - `current`   — the SELECTED plan is exactly the org's current plan → disabled marker.
 *   - `checkout`  — first-time Paddle.js overlay purchase (no live subscription).
 *   - `upgrade`   — in-place proration upgrade (ACTIVE paid subscriber, dearer target).
 *   - `downgrade` — a strictly-lower-priced same-currency target → billing portal.
 *   - `portal`    — every other safe-by-default case (different currency, non-ACTIVE
 *                   subscriber, errored read) → billing portal.
 *
 * The checkout/upgrade/portal decision REUSES `upgradeRowMode` verbatim (the P2
 * eligibility rule); this only adds the exact-plan "current" short-circuit and
 * splits the portal bucket into `downgrade` vs `portal` for the button LABEL.
 *
 * "Current" is EXACT-PLAN, not tier-scoped: a Scale Plus 5k subscriber sliding to
 * 10k, or a monthly subscriber flipping to Annually, is looking at a different SKU
 * and must see Upgrade/Downgrade. Which one is decided purely by `basePrice` — the
 * TOTAL charged per term, same currency — mirroring backend `canUpgrade`, so for an
 * org on a BASE plan the label cannot disagree with what `PATCH /billing/subscription`
 * will accept. Deliberately NOT per-month-normalized: 5k yearly bills more per term
 * than 5k monthly and is an upgrade, even though its monthly-equivalent rate is lower.
 *
 * The one case where label and backend CAN disagree is an org on a CUSTOM plan: that
 * plan isn't in `GET /billing/plans`, so the caller resolves `parentId ?? id` and this
 * compares against the BASE plan's price, while the backend compares against the custom
 * plan's own. A target priced between the two reads Upgrade and then 041001s in the
 * preview — a dead-end dialog, never a bad charge.
 *
 * GUARDRAIL — same rule as `upgradeRowMode`: the `currency` / `basePrice`
 * comparison below reads the CATALOG USD snapshot on purpose, because the label
 * it produces has to agree with what `PATCH /billing/subscription` will accept.
 * A localized `PricePreview` amount must never reach it — that would make the
 * Upgrade/Downgrade wording depend on an FX rate.
 */
export type PlanCardCtaKind = 'current' | 'checkout' | 'upgrade' | 'downgrade' | 'portal' | 'managed';

export function planCardCta(params: {
  selectedPlan: BillingPlan | null;
  currentPlan: BillingPlan | null;
  subscription: Subscription | null | undefined;
  subscriptionErrored: boolean;
}): PlanCardCtaKind {
  const { selectedPlan, currentPlan, subscription, subscriptionErrored } = params;

  if (selectedPlan == null) return 'portal';
  if (currentPlan != null && selectedPlan.id === currentPlan.id) return 'current';

  const mode = upgradeRowMode({ target: selectedPlan, currentPlan, subscription, subscriptionErrored });
  if (mode === 'managed') return 'managed';
  if (mode === 'checkout') return 'checkout';
  if (mode === 'upgrade') return 'upgrade';

  // `portal` mode — label a strictly-lower-priced, same-currency target "Downgrade";
  // everything else stays a neutral "Manage billing" portal CTA.
  if (
    currentPlan != null &&
    selectedPlan.currency === currentPlan.currency &&
    selectedPlan.basePrice != null &&
    currentPlan.basePrice != null &&
    Number(selectedPlan.basePrice) < Number(currentPlan.basePrice)
  ) {
    return 'downgrade';
  }
  return 'portal';
}
